import React, { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { useAppDispatch } from "hooks/redux";
import { useTranslation } from "react-i18next";

import { StatusCode } from "api/protocols";
import Callout from "components/callout/Callout";
import Sheet from "components/sheet/Sheet";
import { Select } from "components/select/index";
import Spin from "components/spin/spin";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faMinus, faPlus } from "@fortawesome/pro-solid-svg-icons";
import { faFileCirclePlus } from "@fortawesome/pro-thin-svg-icons";
import { faArrowDown, faArrowLeft, faBan, faCartShopping, faCircleCheck, faCircleInfo, faClock, faEllipsisVertical, faEnvelope, faLocationDot, faMagnifyingGlass, faPhone, faPlateUtensils, faTrashCan, faUsdCircle, faUser, faUserXmark, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { Badge } from "components/badge/Badge";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import CardSection from "components/cardSection/CardSection";
import Checkbox from "components/form/checkbox/Checkbox";
import { ButtonNew as Button } from "components/buttonNew/index";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import Toggle from "components/form/toggle/Toggle";
import TextField from "components/form/textField/TextField";
import Note from "components/note/Note";
import NewCustomer, { ICustomerInfoState } from "components/newCustomer/NewCustomer";

import moment from "moment";
import { customerErrorMessage, formatDate, valueToString, convertTime } from "helpers/Helpers";
import { dequeue, enqueue, IUIActions, showError } from "redux/actions/ui";
import { ILocation, IModule, IReservationBooking, IReservationBookingNote, ISegment } from "redux/reducers/models/reservations";
import { ICart } from "redux/reducers/models/cart";
import { IOrder } from "redux/reducers/models/order";
import { ICustomer } from "redux/reducers/models/customer";
import { IAuthState } from "redux/reducers/auth";
import classNames from "classnames";

import {
  StripeCardCvcElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardNumberElementChangeEvent,
} from "@stripe/stripe-js";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import { PostCart } from "api/rpc/2024-04/facilityAdmin/order/cart/cart";
import { GetOrder } from "api/rpc/2024-04/facilityAdmin/order/order";
import { DeleteLineItemToCart, PostLineItemToCart, PutLineItemToCart } from "api/rpc/2024-04/facilityAdmin/order/cart/lineItem";
import CartMenuNew from "pages/secure/facility/register/registerNew/CartMenuNew";
import RegisterContext from "pages/secure/facility/register/registerNew/RegisterContext";
import OrderCompletePopup from "components/orderCompletePopup/OrderCompletePopup";
import { StateType } from "pages/secure/facility/teesheet/newTeeSheetDetails/NewTeeSheetDetails";

import "./reservation.scss";
import Portal from "elements/Portal";
import {
  GetReservationBooking,
  GetReservationBookingNoteAll,
  GetReservationSegment,
  PostReservationBookingNote,
  MoveReservationBooking,
  PutAcceptReservationBooking,
  PutCancelReservationBooking,
  PutReservationBooking,
  PutReservationBookingConfirmation,
} from "api/rpc/2024-04/facilityAdmin/reservation/reservation";
import {
  GetCustomer,
  GetCustomerNotes,
  GetCustomerPaymentMethod,
  PostCustomer,
  PostPaymentMethod,
  PostSetup,
} from "api/rpc/2024-04/facilityAdmin/customer/customer";
import { TCustomerNote } from "../customer/context/contextTypes";
import axios, { CancelToken } from "axios";
import ReactDOM from "react-dom";
import ReservationTableSelection from "./reservationTableSelection/ReservationTableSelection";
import { GetTable, ITable, PutOpenTable } from "api/rpc/2024-04/facilityAdmin/tables/tables";
import { loadActiveTableNew } from "redux/actions/cart";
import { useHistory } from "react-router";
import {
  clampReservationGuestCount,
  createEmptyGuestSearchState,
  getEffectiveReservationMaxBookingSize,
  resizeGuestSearchState,
} from "../settings/reservations/reservationGuestCountSelector/reservationBookingSize";
import { useReservationGuestSearch } from "../settings/reservations/reservationGuestCountSelector/useReservationGuestSearch";

interface IProps {
  uiActions: IUIActions;
  authStore: IAuthState;
  date: Date;
  module: IModule;
  segment: ISegment;
  location: ILocation;
  locations: Array<ILocation>;
  open: boolean;
  durations: { [key: string]: Array<number> };
  durationKey: string;
  onSave: (useGlobalLoader?: boolean) => void;
  onCancel: () => void;
  onOpenTableSuccess: (tableId: number) => void;
  booking: IReservationBooking;
  segments: Array<ISegment> | null;
}

interface IBookingState {
  guestsAmount: number;
  durationKey: string;
  durations: { [key: string]: Array<number> };
  selectedTab: number;
  searching: boolean;
  bookingParticipants: Array<Partial<ICustomer & { participant_id: number; guest?: number; check_in_status?: string }>>;
  guestQueries: string[];
  guestSearchResults: Array<ICustomer[]>;
  selectedCardId: number;
  customerCreditCards: Array<Record<string, any>>;
  elementComplete: {
    cardNumber: boolean;
    cardExpiry: boolean;
    cardCvc: boolean;
  };
  saveCard: boolean;
  addCard: boolean;
  cancelPopoverVisible: boolean;
  confirmationPopoverVisible: boolean;
  addNewNoteVisible: boolean;
  checkInStatus: string;
  financialStatus: string;
  note: string;
  bookingNotes: Array<IReservationBookingNote>;
  [key: string]: any;
  copyPopoverVisible: boolean;
  copy_location_id: number;
  copy_date: Date | null;
  copy_time: string;
  tableId: number;
  table: ITable;
  sendCancellationEmail: boolean;
}

interface INewCustomerState {
  newCustomerSheetActive: boolean;
}

interface IAvailableSegment extends ISegment {
  availableHours: number;
}

enum Page {
  EDIT_BOOKING = 1,
  OPEN_TABLE = 2,
}

function getSegmentFee(segment?: ISegment | null) {
  const block = segment?.blocks?.find(item => item?.product_variant_id);
  if (block?.product_variant_id) {
    return {
      variantId: block.product_variant_id,
      productTitle: block.product_title,
      variantTitle: block.variant_title,
      price: block.variant_price,
    };
  }

  if (segment?.product_variant_id) {
    return {
      variantId: segment.product_variant_id,
      productTitle: segment.product_title,
      variantTitle: segment.variant_title,
      price: segment.variant_price != null ? Number(segment.variant_price) : undefined,
    };
  }

  return null;
}

type TFeeOption = {
  variantId?: number;
  productTitle?: string;
  variantTitle?: string;
};

type TSegmentFeeGroup = {
  variantId?: number;
  productTitle?: string;
  variantTitle?: string;
  quantity: number;
  timeLabel: string;
  options: TFeeOption[];
};

function segmentFeeOptions(segment: ISegment): TFeeOption[] {
  const options = (segment.blocks ?? [])
    .filter(block => block.product_variant_id)
    .map(block => ({
      variantId: block.product_variant_id,
      productTitle: block.product_title,
      variantTitle: block.variant_title,
    }));
  const current = getSegmentFee(segment);
  if (current?.variantId && !options.some(option => option.variantId === current.variantId)) {
    options.unshift(current);
  }
  return options;
}

function mergeFeeOptions(current: TFeeOption[], incoming: TFeeOption[]) {
  incoming.forEach(option => {
    if (!current.some(item => item.variantId === option.variantId)) {
      current.push(option);
    }
  });
}

function groupFeeOptions(fee: TSegmentFeeGroup): TFeeOption[] {
  const options = fee.options?.length
    ? fee.options
    : [{ variantId: fee.variantId, productTitle: fee.productTitle, variantTitle: fee.variantTitle }];
  return options.filter(option => option.variantId);
}

function segmentDurationMinutes(segment: ISegment) {
  return segment.blocks?.find(block => block.duration_minutes)?.duration_minutes || 30;
}

function formatFeeTimeRange(startTime?: string, endTime?: string) {
  const startMoment = moment(startTime, ["HH:mm:ss", "HH:mm"], true);
  const endMoment = moment(endTime, ["HH:mm:ss", "HH:mm"], true);
  if (!startMoment.isValid()) {
    return "";
  }
  if (!endMoment.isValid()) {
    return startMoment.format("h:mm A");
  }
  return `${startMoment.format("h:mm")} - ${endMoment.format("h:mm A")}`;
}

function getBookedFeeGroups(segments: Array<ISegment> | null | undefined, segmentIds: Array<number> | undefined): TSegmentFeeGroup[] {
  if (!segmentIds?.length || !segments?.length) {
    return [];
  }

  const bookedSegments = segmentIds
    .map(id => segments.find(item => item.id === id))
    .filter((item): item is ISegment => Boolean(item))
    .sort((left, right) => left.start_time.localeCompare(right.start_time));

  const groups: Array<TSegmentFeeGroup & { startTime: string; endTime: string }> = [];

  bookedSegments.forEach(bookedSegment => {
    const fee = getSegmentFee(bookedSegment);
    const endTime = moment(bookedSegment.start_time, ["HH:mm:ss", "HH:mm"], true)
      .add(segmentDurationMinutes(bookedSegment), "minutes")
      .format("HH:mm:ss");
    const current = groups[groups.length - 1];
    const continuesGroup =
      current &&
      current.variantId === fee?.variantId &&
      moment(current.endTime, "HH:mm:ss", true).format("HH:mm") ===
        moment(bookedSegment.start_time, ["HH:mm:ss", "HH:mm"], true).format("HH:mm");

    if (continuesGroup) {
      current.quantity += 1;
      current.endTime = endTime;
      current.timeLabel = formatFeeTimeRange(current.startTime, endTime);
      mergeFeeOptions(current.options, segmentFeeOptions(bookedSegment));
      return;
    }

    groups.push({
      variantId: fee?.variantId,
      productTitle: fee?.productTitle,
      variantTitle: fee?.variantTitle,
      quantity: 1,
      options: segmentFeeOptions(bookedSegment),
      startTime: bookedSegment.start_time,
      endTime,
      timeLabel: formatFeeTimeRange(bookedSegment.start_time, endTime),
    });
  });

  return groups.map(group => ({
    variantId: group.variantId,
    productTitle: group.productTitle,
    variantTitle: group.variantTitle,
    quantity: group.quantity,
    timeLabel: group.timeLabel,
    options: group.options,
  }));
}

const MINUTES_PER_SEGMENT = 30;

function buildDurationsFromStart(segments: ISegment[], startTime: string, bookingId?: number) {
  const startIndex = segments.findIndex(item => item.start_time === startTime);
  if (startIndex < 0) {
    return {};
  }

  const available = segments.slice(startIndex);
  const max = Math.min(10, available.length);
  let minutes = 0;
  const nextDurations: { [key: string]: number[] } = {};

  for (let i = 0; i < max; i++) {
    const candidate = available[i];
    const heldByBooking = Boolean(bookingId && candidate.booking_id === bookingId);
    if (!((candidate.blocked_type === "open" && !candidate.booking_id) || heldByBooking)) {
      break;
    }
    minutes += MINUTES_PER_SEGMENT;
    const hours = minutes / 60;
    const timeKey = `${hours} ${hours > 1 ? "Hours" : "Hour"}`;
    nextDurations[timeKey] = available.slice(0, i + 1).map(item => item.id);
  }

  return nextDurations;
}

function ChangeGuestCard(props: {
  name?: string;
  customerType?: string;
  memberCode?: string;
  phone?: string;
  email?: string;
  onClear?: () => void;
  clearLabel?: string;
}) {
  const subtitle = [props.customerType, props.memberCode].filter(Boolean).join(" • ");
  const hasContact = Boolean(props.phone || props.email);

  return (
    <div className="reservation-change-guest-card">
      <span className="reservation-change-guest-card__avatar">
        <FontAwesomeIcon icon={faUser} />
      </span>
      <div className="reservation-change-guest-card__identity">
        <div className="reservation-change-guest-card__name">{props.name}</div>
        {subtitle ? <div className="reservation-change-guest-card__meta">{subtitle}</div> : null}
      </div>
      {hasContact ? <span className="reservation-change-guest-card__divider" /> : null}
      {hasContact ? (
        <div className="reservation-change-guest-card__contact">
          {props.phone ? (
            <div>
              <FontAwesomeIcon icon={faPhone} />
              <span>{props.phone}</span>
            </div>
          ) : null}
          {props.email ? (
            <div>
              <FontAwesomeIcon icon={faEnvelope} />
              <span>{props.email}</span>
            </div>
          ) : null}
        </div>
      ) : null}
      {props.onClear ? (
        <button type="button" className="reservation-change-guest-card__clear" aria-label={props.clearLabel} onClick={props.onClear}>
          <FontAwesomeIcon icon={faXmark} />
        </button>
      ) : null}
    </div>
  );
}

function mapReservationParticipants(nextBooking?: IReservationBooking | null) {
  return (nextBooking?.participants ?? []).map(participant => ({
    ...participant,
    participant_id: participant?.id,
    id: participant?.customer_id,
    email: participant?.customer?.email,
    customer_type: participant?.customer?.customer_type,
    member_code: participant?.customer?.member_code,
    member_number: participant?.customer?.member_number,
  }));
}

function formatDurationLabel(durationKey: string) {
  const hours = parseFloat(durationKey);
  if (!Number.isFinite(hours)) {
    return durationKey;
  }
  if (hours === 1) {
    return "1 hour";
  }
  return Number.isInteger(hours) ? `${hours} hours` : `${hours} hour`;
}

function pickDurationKey(
  nextDurations: { [key: string]: number[] },
  preferredKey: string,
  preferredLength?: number,
) {
  if (nextDurations[preferredKey]) {
    return preferredKey;
  }
  const sameLength = Object.entries(nextDurations).find(([, ids]) => ids.length === preferredLength);
  if (sameLength) {
    return sameLength[0];
  }
  const keys = Object.keys(nextDurations);
  return keys[keys.length - 1] ?? "";
}

export default function ReservationBookingModalEdit(props: IProps) {
  const history = useHistory();
  const {
    uiActions,
    date,
    module,
    onSave,
    onCancel,
    open,
    durations,
    segment,
    location,
    locations,
    durationKey,
    onOpenTableSuccess,
    booking,
    segments,
  } = props;
  const [bookingState, setBookingState] = useState<IBookingState>(() => ({
    guestsAmount: 1,
    durationKey: "",
    durations: null,
    selectedTab: 0,
    searching: false,
    bookingParticipants: [],
    ...createEmptyGuestSearchState<ICustomer>(getEffectiveReservationMaxBookingSize(module?.max_booking_size)),
    selectedCardId: undefined,
    customerCreditCards: [],
    elementComplete: {
      cardNumber: false,
      cardExpiry: false,
      cardCvc: false,
    },
    saveCard: true,
    addCard: false,
    cancelPopoverVisible: false,
    confirmationPopoverVisible: false,
    checkInStatus: "",
    financialStatus: "",
    note: "",
    bookingNotes: [],
    addNewNoteVisible: false,
    copyPopoverVisible: false,
    copy_location_id: 0,
    copy_date: date,
    copy_time: "",
    tableId: null,
    table: null,
    sendCancellationEmail: true,
  }));

  const [newCustomerState, setNewCustomerState] = useState<INewCustomerState>({
    newCustomerSheetActive: false,
  });

  const [editModalPage, setEditModalPage] = useState<Page>(Page.EDIT_BOOKING);
  const [editDate, setEditDate] = useState<Date>(date);
  const [editFormOpen, setEditFormOpen] = useState(false);
  const [addGuestOpen, setAddGuestOpen] = useState(false);
  const [addGuestCount, setAddGuestCount] = useState(1);
  const [pendingGuests, setPendingGuests] = useState<Array<ICustomer | null>>([]);
  const newCustomerSlotRef = useRef<number | null>(null);
  const [moveBookingKey, setMoveBookingKey] = useState(0);
  const [removeGuestIndex, setRemoveGuestIndex] = useState<number | null>(null);
  const [changeGuestIndex, setChangeGuestIndex] = useState<number | null>(null);
  const [changeGuestCustomer, setChangeGuestCustomer] = useState<ICustomer | null>(null);
  const [changeGuestResults, setChangeGuestResults] = useState<ICustomer[]>([]);
  const [openedTableId, setOpenedTableId] = useState<number | null>(null);
  const [editDaySegments, setEditDaySegments] = useState<ISegment[]>([]);
  const [editStartTime, setEditStartTime] = useState("");
  const [editDurations, setEditDurations] = useState<{ [key: string]: number[] } | null>(null);
  const [editFormSnapshot, setEditFormSnapshot] = useState<{
    guestsAmount: number;
    durationKey: string;
    bookingParticipants: Array<Partial<ICustomer & { participant_id: number; guest?: number; check_in_status?: string }>>;
  } | null>(null);

  const [openTableState, setOpenTableState] = useState<{ tableId: number; seatsAmount: number }>({
    tableId: null,
    seatsAmount: 1,
  });
  const [orderCart, setOrderCart] = useState<ICart | null>(null);
  const [completedOrder, setCompletedOrder] = useState<IOrder | null>(null);
  const [bookingOrder, setBookingOrder] = useState<IOrder | null>(null);
  const [orderCompletePopup, setOrderCompletePopup] = useState({
    isOpen: false,
    clearOnClose: true,
    loading: false,
    transactionComplete: false,
  });
  const [addingFee, setAddingFee] = useState(false);
  const [addingFeeTarget, setAddingFeeTarget] = useState<string | null>(null);
  const [checkingInIndex, setCheckingInIndex] = useState<number | null>(null);
  const orderCartRef = useRef<ICart | null>(null);
  const guestSyncBookingIdRef = useRef<number | null>(null);
  const guestsListRef = useRef<HTMLDivElement>(null);
  const addingFeeRef = useRef(false);
  const markedPaidRef = useRef(false);
  const orderLookupRef = useRef<number | null>(null);
  const cardBookingIdRef = useRef<number | null>(null);

  const [allAvailableSegments, setAllAvailableSegments] = useState([]);
  const availableSegments = useMemo(() => {
    const returnData: Partial<IAvailableSegment>[] = [];

    allAvailableSegments.map((availableSegment: ISegment) => {
      if (availableSegment.blocked_type === "open" || availableSegment.booking_id === segment?.booking?.id) {
        returnData.push({
          availableHours: getAvailableHours(availableSegment),
          id: availableSegment.id,
          start_time: availableSegment.start_time,
          tournament_id: availableSegment.tournament_id,
          league_id: availableSegment.league_id,
        });
      }
    });

    return returnData;
  }, [allAvailableSegments, segment?.booking?.id]);

  const dispatch = useAppDispatch();
  const { Option } = Select;
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation();

  const [highPriorityNotes, setHighPriorityNotes] = useState<TCustomerNote[]>([]);
  const maxBookingSize = useMemo(() => getEffectiveReservationMaxBookingSize(module?.max_booking_size), [module?.max_booking_size]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    if (!open) {
      guestSyncBookingIdRef.current = null;
      cardBookingIdRef.current = null;
      return;
    }
    if (open && segment) {
      setEditDate(date);
      void getBookingNotes();
      void getHighPriorityCustomerNotes(source.token);
      void loadCustomerPaymentMethods();
      if (segment?.booking?.table_id) {
        void getTable(segment?.booking?.table_id, source.token);
      }

      const bookingId = segment.booking_id ?? booking?.id ?? null;
      const openingBooking = cardBookingIdRef.current !== bookingId;
      if (openingBooking) {
        cardBookingIdRef.current = bookingId;
      }
      const shouldSyncGuests = guestSyncBookingIdRef.current !== bookingId && Boolean(booking?.participants);
      if (shouldSyncGuests) {
        guestSyncBookingIdRef.current = bookingId;
      }
      const participants = shouldSyncGuests ? mapReservationParticipants(booking) : null;
      const savedCardId = booking?.customer_payment_method_id ?? segment?.booking?.customer_payment_method_id;
      setBookingState(prevState => ({
        ...prevState,
        guestsAmount: shouldSyncGuests
          ? clampReservationGuestCount(segment?.booking?.quantity ?? booking?.quantity, maxBookingSize)
          : prevState.guestsAmount,
        selectedCardId: openingBooking ? (savedCardId ?? undefined) : prevState.selectedCardId,
        durationKey,
        checkInStatus: segment?.booking?.check_in_status,
        financialStatus: segment?.booking?.order_financial_status,
        tableId: segment?.booking?.table_id,
        bookingParticipants: participants ?? prevState.bookingParticipants,
      }));
    }
  }, [open, segment, durationKey, booking, maxBookingSize]);

  const reservationOrderId = completedOrder?.id ?? bookingOrder?.id ?? booking?.order_id ?? segment?.booking?.order_id;
  const reservationOrder =
    completedOrder?.id === reservationOrderId
      ? completedOrder
      : bookingOrder?.id === reservationOrderId
        ? bookingOrder
        : null;
  const reservationOrderName =
    reservationOrder?.name ?? booking?.order_name ?? segment?.booking?.order_name;
  const reservationOrderStatus =
    reservationOrder?.financial_status ??
    (bookingState.financialStatus || booking?.order_financial_status || segment?.booking?.order_financial_status);
  const paidVariantIds = useMemo(() => {
    if (reservationOrderStatus !== "paid") {
      return new Set<number>();
    }
    return new Set(
      (reservationOrder?.line_items ?? [])
        .map(item => item.variant_id)
        .filter((variantId): variantId is number => Boolean(variantId)),
    );
  }, [reservationOrder?.line_items, reservationOrderStatus]);

  useEffect(() => {
    if (
      !open ||
      !reservationOrderId ||
      completedOrder?.line_items?.length ||
      (bookingOrder?.id === reservationOrderId && bookingOrder.line_items?.length)
    ) {
      return;
    }
    let cancelled = false;
    void (async () => {
      const orderRes = await GetOrder({ id: reservationOrderId, extended: true }, false);
      if (cancelled || orderRes.status !== StatusCode.OK) {
        return;
      }
      const order = (Array.isArray(orderRes.data) ? orderRes.data[0] : orderRes.data) as IOrder;
      if (order?.id) {
        setBookingOrder(order);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, reservationOrderId, completedOrder?.line_items?.length, bookingOrder?.id, bookingOrder?.line_items?.length]);

  useEffect(() => {
    if (!open || reservationOrderId || reservationOrderStatus !== "paid") {
      return;
    }
    const bookingId = segment?.booking_id;
    const customerId = booking?.customer_id ?? segment?.booking?.customer_id;
    if (!bookingId || !customerId || orderLookupRef.current === bookingId) {
      return;
    }

    const variantIds = new Set<number>();
    const feeSegments = [...(segments ?? [])];
    if (segment && !feeSegments.some(item => item.id === segment.id)) {
      feeSegments.push(segment);
    }
    feeSegments.forEach(item => {
      segmentFeeOptions(item).forEach(option => {
        if (option.variantId) {
          variantIds.add(option.variantId);
        }
      });
    });
    if (!variantIds.size) {
      return;
    }

    orderLookupRef.current = bookingId;
    let cancelled = false;
    let finished = false;
    void (async () => {
      const orderRes = await GetOrder({ customer_id: customerId, extended: true, limit: 50 }, false);
      if (cancelled || orderRes.status !== StatusCode.OK) {
        return;
      }
      const orders = (Array.isArray(orderRes.data) ? orderRes.data : orderRes.data ? [orderRes.data] : []) as IOrder[];
      const match = orders
        .filter(order => {
          if (order?.financial_status !== "paid") {
            return false;
          }
          return (order.line_items ?? []).some(item => item.variant_id && variantIds.has(Number(item.variant_id)));
        })
        .sort((left, right) => (right.id ?? 0) - (left.id ?? 0))[0];
      if (!match?.id || cancelled) {
        return;
      }
      finished = true;
      setBookingOrder(match);
      void markBookingPaid(match);
    })();
    return () => {
      cancelled = true;
      if (!finished) {
        orderLookupRef.current = null;
      }
    };
  }, [open, reservationOrderId, reservationOrderStatus, segment, segments, booking?.customer_id]);

  useEffect(() => {
    if (!editFormOpen || !location?.id || !module?.id) {
      return;
    }
    let cancelled = false;
    void (async () => {
      const segmentRes = await GetReservationSegment(
        {
          module_id: module.id,
          location_id: location.id,
          date: formatDate(editDate),
        },
        false,
      );
      if (cancelled || segmentRes.status !== StatusCode.OK) {
        return;
      }
      const daySegments = segmentRes.data;
      const startTime = editStartTime || segment?.booking?.start_time || segment?.start_time || "";
      const nextDurations = buildDurationsFromStart(daySegments, startTime, segment?.booking?.id);
      setEditDaySegments(daySegments);
      setEditDurations(Object.keys(nextDurations).length ? nextDurations : null);
      if (!editStartTime && startTime) {
        setEditStartTime(startTime);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editFormOpen, editDate, location?.id, module?.id]);

  useEffect(() => {
    setBookingState(prevState => {
      if (prevState.guestQueries.length === maxBookingSize) {
        return prevState;
      }

      const resizedGuestSearch = resizeGuestSearchState(
        prevState.guestQueries,
        prevState.guestSearchResults,
        maxBookingSize,
      );

      return {
        ...prevState,
        ...resizedGuestSearch,
      };
    });
  }, [maxBookingSize]);

  async function getTable(tableId: number, cancelToken: CancelToken) {
    const res = await GetTable({ id: tableId }, false, cancelToken);
    if (res?.status !== StatusCode.OK || (res?.data && res?.data?.length === 0)) {
      return;
    }
    setBookingState(prev => ({ ...prev, table: res?.data[0] }));
  }

  async function searchCustomers(params: string) {
    const customerRes = await GetCustomer({ search: params }, false);

    if (customerRes.status !== StatusCode.OK) {
      console.log(customerRes?.message);
      return;
    }

    return customerRes?.data;
  }

  const handleGuestSearchResults = useCallback((slotIndex: number, results: ICustomer[]) => {
    setBookingState(prevState => {
      const guestSearchResults = [...prevState.guestSearchResults];
      guestSearchResults[slotIndex] = results;
      return { ...prevState, guestSearchResults };
    });
  }, []);

  const handleGuestSearching = useCallback((searching: boolean) => {
    setBookingState(prevState => ({ ...prevState, searching }));
  }, []);

  useReservationGuestSearch({
    guestQueries: bookingState.guestQueries,
    maxSlots: maxBookingSize,
    enabled: open,
    searchCustomers,
    onSlotResults: handleGuestSearchResults,
    onSearching: handleGuestSearching,
  });

  useEffect(() => {
    if (!bookingState.copyPopoverVisible || !bookingState.copy_date || !bookingState.copy_location_id) {
      return;
    }
    void loadTimes();
  }, [bookingState.copyPopoverVisible, bookingState.copy_date, bookingState.copy_location_id]);

  async function getBookingNotes() {
    const notesRes = await GetReservationBookingNoteAll({ reservation_booking_id: segment?.booking_id }, true);
    if (notesRes.status !== StatusCode.OK) {
      uiActions.showError(t("secure.facility.reservation.reservation_booking_modal_edit.039"));
      return;
    }
    setBookingState(prevState => ({ ...prevState, bookingNotes: notesRes.data }));
  }

  async function getHighPriorityCustomerNotes(token?: CancelToken) {
    const bookingCustomerID = segment.booking?.customer_id;
    const highPriorityNotesRes = await GetCustomerNotes(
      {
        customer_id: bookingCustomerID,
        high_priority: true,
      },
      true,
      token,
    );

    if (token && token.reason) {
      return;
    }
    setHighPriorityNotes(highPriorityNotesRes.data);
  }

  async function resendReservationConfirmation() {
    const confirmationRes = await PutReservationBookingConfirmation(
      { reservation_booking_id: segment?.booking_id },
      true,
    );

    if (confirmationRes?.status !== StatusCode.OK) {
      uiActions.showError(t("secure.facility.reservation.reservation_booking_modal_edit.047"));
      return;
    }

    const holderName = bookingState.bookingParticipants?.[0]?.full_name || "";
    uiActions.showSuccess(
      t("Confirmation email resent successfully to {{name}} (Booking holder)", { name: holderName }),
    );
  }

  async function loadTimes() {
    const segmentRes = await GetReservationSegment(
      {
        module_id: module?.id,
        location_id: bookingState.copy_location_id,
        date: moment(bookingState.copy_date ?? undefined).format("YYYY-MM-DD"),
      },
      false,
    );

    if (segmentRes.status !== StatusCode.OK) {
      uiActions.showError(t("secure.facility.reservation.reservation_booking_modal_edit.052"));
      return;
    }

    setAllAvailableSegments(segmentRes.data);
  }

  async function moveBooking() {
    if (!bookingState.copy_date || !bookingState.copy_location_id || !bookingState.copy_time || !segment?.booking?.id) {
      return;
    }

    const segmentCount = durations?.[bookingState.durationKey]?.length ?? 0;
    const destinationSegments = allAvailableSegments as ISegment[];
    const startIndex = destinationSegments.findIndex(
      availableSegment => availableSegment.start_time === bookingState.copy_time,
    );
    const segmentIds =
      startIndex >= 0
        ? destinationSegments.slice(startIndex, startIndex + segmentCount).map(availableSegment => availableSegment.id)
        : [];

    if (!segmentCount || segmentIds.length !== segmentCount) {
      uiActions.showError(t("Could not move booking"));
      return;
    }

    const moveRes = await MoveReservationBooking(
      {
        booking_id: segment.booking.id,
        location_id: bookingState.copy_location_id,
        date: formatDate(bookingState.copy_date),
        segment_ids: segmentIds,
      },
      true,
    );

    if (moveRes?.status !== StatusCode.OK) {
      uiActions.showError(typeof moveRes?.data === "string" ? moveRes.data : t("Could not move booking"));
      return;
    }

    closeMoveBooking();
    void onSave();
    void closeBookingModal();
    uiActions.showSuccess(t("Booking moved successfully"));
  }

  function handleCancelBooking() {
    setBookingState(prevState => ({
      ...prevState,
      cancelPopoverVisible: true,
      confirmationPopoverVisible: false,
      copyPopoverVisible: false,
    }));
  }

  function openMoveBooking() {
    setMoveBookingKey(key => key + 1);
    setBookingState(prevState => ({
      ...prevState,
      copyPopoverVisible: true,
      cancelPopoverVisible: false,
      confirmationPopoverVisible: false,
      copy_date: null,
      copy_location_id: 0,
      copy_time: "",
    }));
  }

  function closeMoveBooking() {
    setBookingState(prevState => ({
      ...prevState,
      copyPopoverVisible: false,
    }));
  }

  function syncDateWithState(returnDate: Date) {
    setBookingState(prevState => ({ ...prevState, copy_date: returnDate, copy_time: "" }));
  }

  function handleTimeChange(incomingValue: string) {
    setBookingState(prevState => ({
      ...prevState,
      copy_time: incomingValue,
    }));
  }

  function getAvailableHours(availableSegment: ISegment) {
    let availableHours = 0;
    const destinationSegments = allAvailableSegments as ISegment[];
    const index = destinationSegments.findIndex(candidate => candidate.id === availableSegment.id);
    const bookingId = segment?.booking?.id;

    for (let i = index; i < destinationSegments.length; i++) {
      const candidate = destinationSegments[i];
      if (candidate.blocked_type === "open" || (bookingId && candidate.booking_id === bookingId)) {
        availableHours++;
      } else {
        break;
      }
    }

    return availableHours;
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { id, value } = e.target;
    setBookingState(prevState => ({ ...prevState, [id]: value }));
  }

  async function loadCustomerPaymentMethods(useGlobalLoader = true, ensureCardId?: number | null) {
    const customerId = booking?.customer_id ?? segment?.booking?.customer_id;
    const paymentMethodRes = await GetCustomerPaymentMethod({ customer_id: customerId }, useGlobalLoader);
    if (paymentMethodRes?.status !== StatusCode.OK) {
      uiActions.showError(t("secure.facility.reservation.reservation_booking_modal_edit.003"));
      return;
    }
    const cards = Array.isArray(paymentMethodRes.data) ? [...paymentMethodRes.data] : [];
    const selectedId =
      ensureCardId ?? booking?.customer_payment_method_id ?? segment?.booking?.customer_payment_method_id;
    if (selectedId && !cards.some(card => Number(card.id) === Number(selectedId))) {
      const selectedRes = await GetCustomerPaymentMethod({ customer_id: customerId, id: selectedId }, false);
      const selectedCard = Array.isArray(selectedRes?.data) ? selectedRes.data[0] : selectedRes?.data;
      if (selectedRes?.status === StatusCode.OK && selectedCard?.id) {
        cards.unshift(selectedCard);
      }
    }
    setBookingState(prevState => ({ ...prevState, customerCreditCards: cards }));
  }

  function handleDropDownChange(value: string | number, id: string) {
    setBookingState(prevState => ({ ...prevState, [id]: value }));
  }

  function handleCustomerSelection(id: number, customer: ICustomer) {
    const updatedBookingParticipants = [...bookingState.bookingParticipants];
    updatedBookingParticipants.push(customer);
    setBookingState(prevState => ({
      ...prevState,
      ...createEmptyGuestSearchState<ICustomer>(maxBookingSize),
      bookingParticipants: updatedBookingParticipants,
    }));
  }

  function handleCardSectionChange(
    e: StripeCardNumberElementChangeEvent | StripeCardExpiryElementChangeEvent | StripeCardCvcElementChangeEvent,
  ) {
    if (bookingState.selectedCardId !== undefined) {
      setBookingState(prevState => ({ ...prevState, selectedCardId: undefined }));
    }

    setBookingState(prevState => ({
      ...prevState,
      elementComplete: { ...prevState.elementComplete, [e.elementType]: e.complete },
    }));
  }

  async function saveBookingCard(customerPaymentMethodId: number | null) {
    const bookingId = segment?.booking_id ?? booking?.id;
    if (!bookingId) {
      uiActions.showError(t("Error making booking"));
      return false;
    }

    const bookingRes = await PutReservationBooking(
      {
        id: bookingId,
        customer_payment_method_id: customerPaymentMethodId,
      },
      false,
    );
    if (bookingRes?.status !== StatusCode.OK) {
      uiActions.showError(typeof bookingRes?.data === "string" ? bookingRes.data : t("Error making booking"));
      return false;
    }

    const savedId = bookingRes.data?.customer_payment_method_id ?? customerPaymentMethodId;
    setBookingState(prevState => ({
      ...prevState,
      selectedCardId: savedId == null ? undefined : Number(savedId),
    }));
    void onSave(false);
    return true;
  }

  async function onSelectCreditCard(id: number) {
    if (!(id > 0)) {
      uiActions.showError(t("secure.facility.reservation.reservation_booking_modal_edit.004"));
      return;
    }

    const previousId = bookingState.selectedCardId;
    const nextId = Number(id) === Number(bookingState.selectedCardId) ? null : Number(id);
    setBookingState(prevState => ({
      ...prevState,
      selectedCardId: nextId ?? undefined,
      addCard: nextId ? false : prevState.addCard,
    }));
    const saved = await saveBookingCard(nextId);
    if (!saved) {
      setBookingState(prevState => ({ ...prevState, selectedCardId: previousId }));
      return;
    }
    uiActions.showSuccess(nextId ? t("Credit card saved") : t("Credit card removed"));
  }

  async function collectCard(customer_id: number) {
    if (!stripe || !elements) {
      uiActions.showError(t("secure.facility.reservation.reservation_booking_modal_edit.005"));
      return;
    }

    // Loader for length of operation to stop user from clicking
    uiActions.enqueue();
    try {
      let setupCardRes;
      const paymentMethod = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement("cardNumber"),
      });

      if (paymentMethod.paymentMethod) {
        setupCardRes = await setupCard(paymentMethod.paymentMethod.id, customer_id);
        uiActions.dequeue();
        return {
          customer_payment_method_id: setupCardRes,
        };
      } else {
        uiActions.showError(paymentMethod?.error?.message);
      }
    } catch {
      uiActions.dequeue();
    }

    uiActions.dequeue();
  }

  async function setupCard(payment_method: string, customer_id: number) {
    const setupRes = await PostSetup({ customer_id }, true);

    if (setupRes.status !== StatusCode.OK) {
      uiActions.showError(String(setupRes?.data?.message));
      return null;
    } else {
      if (payment_method !== "undefined") {
        const clientSecret =
          setupRes?.data?.setup_intent?.client_secret ?? setupRes?.data?.data?.setup_intent?.client_secret;
        if (!clientSecret) {
          uiActions.showError(t("Could not start card setup"));
          return null;
        }
        const stripeRes = await stripe.confirmCardSetup(String(clientSecret), {
          payment_method: payment_method,
        });

        if (stripeRes.error) {
          uiActions.showError(stripeRes?.error?.message);
        } else {
          const methodRes = await PostPaymentMethod({
            payment_method_id: stripeRes?.setupIntent?.payment_method,
            customer_id: customer_id,
            save_card: true,
            save_on_profile: true,
          });
          if (methodRes.status !== StatusCode.OK) {
            uiActions.showError(String(methodRes?.data?.message));
            return null;
          } else {
            const methodId = Number(methodRes?.data?.id ?? methodRes?.data?.data?.id);
            if (!methodId) {
              uiActions.showError(t("Could not save credit card"));
              return null;
            }
            setBookingState(prevState => ({ ...prevState, selectedCardId: methodId }));

            return methodId;
          }
        }
      }
    }
  }

  async function refreshBookingGuests() {
    const bookingId = segment?.booking_id ?? booking?.id;
    if (!bookingId) {
      return;
    }
    const bookingRes = await GetReservationBooking({ id: bookingId, extended: true }, false);
    if (bookingRes?.status !== StatusCode.OK || !bookingRes.data) {
      return;
    }
    const nextBooking = bookingRes.data as IReservationBooking;
    const nextParticipants = mapReservationParticipants(nextBooking);
    setBookingState(prevState => {
      if (!nextParticipants.length && prevState.bookingParticipants.length) {
        return prevState;
      }
      if (nextParticipants.length > prevState.bookingParticipants.length) {
        window.requestAnimationFrame(() => {
          const guestList = guestsListRef.current;
          if (guestList) {
            guestList.scrollTop = guestList.scrollHeight;
          }
        });
      }
      return {
        ...prevState,
        guestsAmount: clampReservationGuestCount(nextBooking.quantity, maxBookingSize),
        bookingParticipants: nextParticipants,
      };
    });
  }

  async function updateBooking(
    stayOnPage = false,
    overrides?: {
      checkInStatus?: string;
      financialStatus?: string;
      bookingParticipants?: IBookingState["bookingParticipants"];
      guestsAmount?: number;
      segmentIds?: number[];
      moveDate?: string;
      successMessage?: string;
      useGlobalLoader?: boolean;
      customerPaymentMethodId?: number | null;
      saveNewCard?: boolean;
    },
  ): Promise<boolean> {
    const selectingSavedCard =
      overrides != null && Object.prototype.hasOwnProperty.call(overrides, "customerPaymentMethodId");
    let customerPaymentMethodId: number | null | undefined = selectingSavedCard
      ? overrides?.customerPaymentMethodId
      : bookingState.selectedCardId;

    const shouldCollectNewCard = !selectingSavedCard && (overrides?.saveNewCard || bookingState.addCard);
    if (overrides?.saveNewCard && !elementsComplete) {
      uiActions.showError(t("Enter the card number, expiry, and CVC."));
      return false;
    }
    if (shouldCollectNewCard && elementsComplete) {
      const newCardRes = await collectCard(bookingState?.bookingParticipants[0]?.id);
      if (!newCardRes?.customer_payment_method_id) {
        if (overrides?.saveNewCard) {
          return false;
        }
      } else {
        customerPaymentMethodId = newCardRes.customer_payment_method_id;
      }
    }

    // Set reservation booking participants
    const participantsSource = overrides?.bookingParticipants ?? bookingState.bookingParticipants;
    const guestQuantity = overrides?.guestsAmount ?? bookingState.guestsAmount;
    const participants = [...participantsSource]?.map(participant => ({
      id: participant?.participant_id,
      customer_id: participant?.id,
      guest: false as boolean,
      check_in_status: participant?.check_in_status || "unchecked",
    }));
    if (guestQuantity > participantsSource?.length) {
      const diff = guestQuantity - participantsSource?.length;
      for (let i = 0; i < diff; i++) {
        participants.push({
          id: null,
          customer_id: participantsSource[0]?.id,
          guest: true,
          check_in_status: "unchecked",
        });
      }
    }

    const nextSegmentIds = overrides?.segmentIds ?? durations[bookingState?.durationKey];
    const originalSegmentIds = durations?.[durationKey] ?? [];
    const moveDate = overrides?.moveDate;
    const dateChanged = Boolean(moveDate && segment?.booking?.date && moveDate !== segment.booking.date);
    if (dateChanged && segment?.booking?.id && moveDate) {
      const originalCount = originalSegmentIds.length;
      const moveIds =
        nextSegmentIds?.length === originalCount
          ? nextSegmentIds
          : Object.values(editDurations ?? {}).find(ids => ids.length === originalCount) ?? [];
      if (moveIds.length !== originalCount) {
        uiActions.showError(t("Could not move booking"));
        return false;
      }
      const moveRes = await MoveReservationBooking(
        {
          booking_id: segment.booking.id,
          location_id: location?.id,
          date: moveDate,
          segment_ids: moveIds,
        },
        true,
      );
      if (moveRes?.status !== StatusCode.OK) {
        uiActions.showError(typeof moveRes?.data === "string" ? moveRes.data : t("Could not move booking"));
        return false;
      }
    }

    const bookingRes = await PutReservationBooking(
      {
        id: segment?.booking_id,
        segment_ids: nextSegmentIds,
        date: formatDate(editDate),
        quantity: guestQuantity,
        ...(selectingSavedCard || customerPaymentMethodId != null
          ? { customer_payment_method_id: customerPaymentMethodId ?? null }
          : {}),
        check_in_status: overrides?.checkInStatus ?? bookingState?.checkInStatus,
        order_financial_status: overrides?.financialStatus ?? bookingState?.financialStatus,
        reservation_participants: participants,
      },
      overrides?.useGlobalLoader !== false,
    );

    if (bookingRes?.status !== StatusCode.OK) {
      uiActions.showError(typeof bookingRes?.data === "string" ? bookingRes?.data : t("Error making booking"));
      return false;
    }

    if (stayOnPage) {
      setEditFormOpen(false);
      setEditFormSnapshot(null);
      setAddGuestOpen(false);
      setAddGuestCount(1);
      setPendingGuests([]);
      await refreshBookingGuests();
      void onSave(overrides?.useGlobalLoader !== false);
      uiActions.showSuccess(overrides?.successMessage ?? t("secure.facility.reservation.reservation_booking_modal_edit.007"));
      return true;
    }

    void closeBookingModal();
    void onSave(overrides?.useGlobalLoader !== false);
    uiActions.showSuccess(overrides?.successMessage ?? t("secure.facility.reservation.reservation_booking_modal_edit.007"));
    return true;
  }

  function closeBookingModal() {
    ReactDOM.unstable_batchedUpdates(() => {
      setBookingState(prevState => ({
        ...prevState,
        guestsAmount: 1,
        selectedTab: 0,
        durationKey: "",
        bookingParticipants: [],
        durations: null,
        ...createEmptyGuestSearchState<ICustomer>(maxBookingSize),
        customerCreditCards: [],
        selectedCardId: undefined,
        saveCard: true,
        addCard: false,
        cancelPopoverVisible: false,
        checkInStatus: "",
        financialStatus: "",
        note: "",
        bookingNotes: [],
        addNewNoteVisible: false,
        tableId: null,
        sendCancellationEmail: true,
      }));
      setEditModalPage(Page.EDIT_BOOKING);
      setEditFormOpen(false);
      setEditFormSnapshot(null);
      setAddGuestOpen(false);
      setAddGuestCount(1);
      setPendingGuests([]);
      setCheckingInIndex(null);
      setOpenTableState({ tableId: null, seatsAmount: 1 });
      setOpenedTableId(null);
      setRemoveGuestIndex(null);
      setCompletedOrder(null);
      setBookingOrder(null);
      orderLookupRef.current = null;
      setOrderCompletePopup({
        isOpen: false,
        clearOnClose: true,
        loading: false,
        transactionComplete: false,
      });
      markedPaidRef.current = false;
      setChangeGuestIndex(null);
      orderCartRef.current = null;
      setOrderCart(null);
    });

    void onCancel();
  }

  const isPending = booking?.status === "pending" || segment?.booking?.status === "pending";
  const bookedSegmentIds = durations?.[bookingState.durationKey] ?? [];
  const feeGroups = getBookedFeeGroups(segments, bookedSegmentIds);
  const displayFeeGroups =
    feeGroups.length > 0
      ? feeGroups
      : [
          {
            ...getSegmentFee(segment),
            quantity: bookedSegmentIds.length || 1,
            options: segment ? segmentFeeOptions(segment) : [],
            timeLabel: formatFeeTimeRange(
              segment?.booking?.start_time || segment?.start_time,
              segment?.booking?.end_time,
            ),
          },
        ];

  function unwrapCart(payload: ICart | { data?: ICart } | null | undefined): ICart | null {
    if (!payload || typeof payload !== "object") {
      return null;
    }
    const record = payload as ICart & { data?: ICart };
    if (Array.isArray(record.line_items) || typeof record.id === "number") {
      return record;
    }
    if (record.data && (Array.isArray(record.data.line_items) || typeof record.data.id === "number")) {
      return record.data;
    }
    return record;
  }

  function rememberOrderCart(cart: ICart | { data?: ICart } | null) {
    const nextCart = cart ? unwrapCart(cart) : null;
    orderCartRef.current = nextCart;
    setOrderCart(nextCart);
  }

  function resetCompletedOrder() {
    if (completedOrder?.id) {
      setBookingOrder(current => current ?? completedOrder);
    }
    setCompletedOrder(null);
    setOrderCompletePopup({
      isOpen: false,
      clearOnClose: true,
      loading: false,
      transactionComplete: false,
    });
    rememberOrderCart(null);
  }

  function asOrder(order: IOrder | { data?: IOrder } | null | undefined): IOrder | null {
    if (!order || typeof order !== "object") {
      return null;
    }
    if ("id" in order && typeof order.id === "number") {
      return order;
    }
    if ("data" in order && order.data && typeof order.data.id === "number") {
      return order.data;
    }
    return null;
  }

  async function markBookingPaid(order?: IOrder | null) {
    const bookingId = segment?.booking_id;
    if (!bookingId) {
      return;
    }

    const nextOrder = asOrder(order);
    const existingOrderId = booking?.order_id ?? segment?.booking?.order_id ?? bookingOrder?.id ?? null;
    const statusPaid =
      bookingState.financialStatus === "paid" ||
      booking?.order_financial_status === "paid" ||
      segment?.booking?.order_financial_status === "paid";
    const orderAlreadyLinked = !nextOrder?.id || nextOrder.id === existingOrderId;
    if ((markedPaidRef.current && statusPaid && orderAlreadyLinked) || (statusPaid && !nextOrder?.id && existingOrderId)) {
      markedPaidRef.current = true;
      return;
    }

    markedPaidRef.current = true;
    const bookingRes = await PutReservationBooking(
      {
        id: bookingId,
        order_financial_status: nextOrder?.financial_status || "paid",
        ...(nextOrder?.id ? { order_id: nextOrder.id, order_name: nextOrder.name } : {}),
      },
      false,
    );

    if (bookingRes?.status !== StatusCode.OK) {
      markedPaidRef.current = false;
      uiActions.showError(typeof bookingRes?.data === "string" ? bookingRes.data : t("Error making booking"));
      return;
    }

    if (nextOrder?.id) {
      setBookingOrder(nextOrder);
    }
    setBookingState(prevState => ({ ...prevState, financialStatus: nextOrder?.financial_status || "paid" }));
    void onSave();
  }

  function updateCartOriginState<T>(newState: Partial<T>, type: StateType) {
    if (type === StateType.OrderComplete) {
      setOrderCompletePopup(current => ({ ...current, ...(newState as Partial<typeof orderCompletePopup>) }));
      return;
    }

    if (type !== StateType.CheckInState || !newState || typeof newState !== "object") {
      return;
    }

    const nextState = newState as { cart?: ICart | null; order?: IOrder | null };
    if ("cart" in nextState) {
      rememberOrderCart(nextState.cart ?? null);
    }
    if ("order" in nextState) {
      const order = asOrder(nextState.order);
      setCompletedOrder(order);
      if (order && (Number(order.balance) <= 0 || order.financial_status === "paid")) {
        setOrderCompletePopup(current => ({
          ...current,
          loading: false,
          transactionComplete: true,
        }));
        void markBookingPaid(order);
      }
    }
  }

  async function addSegmentFeeToCart(fee: TSegmentFeeGroup) {
    if (!fee.variantId || addingFeeRef.current) {
      return;
    }

    addingFeeRef.current = true;

    try {
      let cart = orderCartRef.current;
      if (!cart?.id) {
        const customerId = bookingState.bookingParticipants?.[0]?.id;
        const postCartRes = await PostCart(customerId ? { customer_id: customerId } : {}, false);
        if (postCartRes.status !== StatusCode.OK) {
          uiActions.showError(t("Error creating order"));
          return;
        }
        cart = postCartRes.data as ICart;
        orderCartRef.current = cart;
      }

      const lineRes = await PostLineItemToCart(
        {
          cart_id: cart.id,
          variants: [{ variant_id: fee.variantId, quantity: fee.quantity }],
        },
        false,
      );
      if (lineRes.status !== StatusCode.OK) {
        uiActions.showError(t("Error adding line item to cart"));
        return;
      }

      rememberOrderCart(lineRes.data);
    } finally {
      addingFeeRef.current = false;
    }
  }

  async function addSingleFee(fee: TSegmentFeeGroup) {
    if (!fee.variantId || addingFeeRef.current) {
      return;
    }
    const target = `variant:${fee.variantId}`;
    setAddingFee(true);
    setAddingFeeTarget(target);
    try {
      await addSegmentFeeToCart(fee);
    } finally {
      setAddingFee(false);
      setAddingFeeTarget(null);
    }
  }

  async function removeSegmentFeeFromCart(variantId?: number) {
    const cart = orderCartRef.current;
    const lineItems = cart?.line_items?.filter(item => item.variant_id === variantId) ?? [];
    if (!variantId || !lineItems.length || addingFeeRef.current) {
      return;
    }

    addingFeeRef.current = true;
    setAddingFee(true);
    setAddingFeeTarget(`variant:${variantId}`);

    try {
      let latestCart: ICart | null = cart;
      for (const lineItem of lineItems) {
        const deleteRes = await DeleteLineItemToCart({ id: lineItem.id }, false);
        if (deleteRes.status !== StatusCode.OK) {
          uiActions.showError(t("Error removing line item from cart"));
          return;
        }
        latestCart = deleteRes.data as ICart;
      }
      rememberOrderCart(latestCart);
    } finally {
      addingFeeRef.current = false;
      setAddingFee(false);
      setAddingFeeTarget(null);
    }
  }

  async function addFeesForGroup(fee: TSegmentFeeGroup, groupKey: string) {
    if (addingFeeRef.current) {
      return;
    }
    setAddingFee(true);
    setAddingFeeTarget(groupKey);
    try {
      for (const option of groupFeeOptions(fee)) {
        if (!option.variantId) {
          continue;
        }
        if (
          paidVariantIds.has(option.variantId) ||
          orderCartRef.current?.line_items?.some(item => item.variant_id === option.variantId)
        ) {
          continue;
        }
        await addSegmentFeeToCart({
          ...fee,
          variantId: option.variantId,
          productTitle: option.productTitle,
          variantTitle: option.variantTitle,
        });
      }
    } finally {
      setAddingFee(false);
      setAddingFeeTarget(null);
    }
  }

  async function confirmCancelBooking() {
    const cancelRes = await PutCancelReservationBooking({ id: segment?.booking_id, send_cancellation_email: bookingState?.sendCancellationEmail }, true);
    if (cancelRes?.status !== StatusCode.OK) {
      uiActions.showError(
        isPending
          ? t("Error declining booking request")
          : t("secure.facility.reservation.reservation_booking_modal_edit.008"),
      );
      return;
    }
    uiActions.showSuccess(
      isPending
        ? t("Booking request declined")
        : t("secure.facility.reservation.reservation_booking_modal_edit.009"),
    );
    void closeBookingModal();
    void onSave();
  }

  async function acceptBooking() {
    const bookingRes = await PutAcceptReservationBooking({ id: segment?.booking_id }, true);
    if (bookingRes?.status !== StatusCode.OK) {
      uiActions.showError(
        typeof bookingRes?.data === "string" ? bookingRes.data : t("Error approving booking request"),
      );
      return;
    }
    uiActions.showSuccess(t("Booking request approved"));
    void closeBookingModal();
    void onSave();
  }

  function closeNewCustomer() {
    setNewCustomerState(prevState => ({
      ...prevState,
      newCustomerSheetActive: false,
    }));
  }

  async function createNewCustomer(customerInfo: ICustomerInfoState) {
    if (
      customerInfo.firstName === "" ||
      customerInfo.lastName === "" ||
      (customerInfo.emailAddress === "" && customerInfo.phoneNumber === "")
    ) {
      uiActions.showError(t("secure.facility.reservation.reservation_booking_modal_edit.050"));
      return;
    }

    const customerRes = await PostCustomer(
      {
        first_name: customerInfo.firstName,
        last_name: customerInfo.lastName,
        phone: customerInfo.phoneNumber ?? null,
        email: customerInfo.emailAddress ?? null,
        send_welcome_email: customerInfo.sendWelcomeEmail,
      },
      true,
    );

    if (customerRes.status !== StatusCode.OK) {
      dispatch(showError(customerErrorMessage(t, String(customerRes?.data))));
      return;
    }

    uiActions.showSuccess(t("secure.facility.reservation.reservation_booking_modal_edit.051"));
    const createdCustomer = customerRes?.data as ICustomer;
    const slotIndex = newCustomerSlotRef.current;
    const pendingOffset = slotIndex == null ? -1 : slotIndex - bookingState.guestsAmount;
    if (addGuestOpen && pendingOffset >= 0 && pendingOffset < addGuestCount) {
      selectPendingGuest(pendingOffset, createdCustomer);
    } else {
      void handleCustomerSelection(null, createdCustomer);
    }
    newCustomerSlotRef.current = null;
    void closeNewCustomer();
  }

  const elementsComplete =
    bookingState.elementComplete.cardNumber === true &&
    bookingState.elementComplete.cardExpiry === true &&
    bookingState.elementComplete.cardCvc === true;

  async function saveNewCard() {
    if (!elementsComplete) {
      uiActions.showError(t("Enter the card number, expiry, and CVC."));
      return;
    }
    const customerId = booking?.customer_id ?? segment?.booking?.customer_id ?? bookingState.bookingParticipants?.[0]?.id;
    if (!customerId) {
      uiActions.showError(t("Error making booking"));
      return;
    }
    const previousId = bookingState.selectedCardId;
    const newCardRes = await collectCard(customerId);
    const methodId = Number(newCardRes?.customer_payment_method_id);
    if (!methodId) {
      return;
    }
    const saved = await saveBookingCard(methodId);
    if (!saved) {
      setBookingState(prevState => ({ ...prevState, selectedCardId: previousId }));
      return;
    }
    setBookingState(prevState => ({
      ...prevState,
      addCard: false,
      selectedCardId: methodId,
      elementComplete: { cardNumber: false, cardExpiry: false, cardCvc: false },
    }));
    void loadCustomerPaymentMethods(false, methodId);
    uiActions.showSuccess(t("Credit card saved"));
  }

  async function handleAddBookingNote() {
    const bookingNoteRes = await PostReservationBookingNote(
      { reservation_booking_id: segment?.booking_id, content: bookingState.note },
      true,
    );

    if (bookingNoteRes.status !== StatusCode.OK) {
      uiActions.showError(t("secure.facility.reservation.reservation_booking_modal_edit.049"));
    }

    setBookingState(prevState => ({ ...prevState, addNewNoteVisible: false, note: "" }));
    void getBookingNotes();
  }

  async function handleOpenTableCheckIn() {
    const register = JSON.parse(localStorage.getItem("register"));
    if (!openTableState?.tableId) {
      dispatch(showError(t("Table selection required")));
      return;
    } else if (!openTableState?.seatsAmount || openTableState?.seatsAmount < 0) {
      dispatch(showError(t("Number of seats selected must be greater than 0")));
      return;
    }

    if (register) {
      const tableId = openTableState?.tableId;
      dispatch(enqueue());
      const openTableRes = await PutOpenTable(
        {
          register_id: register?.id,
          table_id: openTableState?.tableId,
          number_of_seats: openTableState?.seatsAmount,
          reservation_customer_id: segment?.booking?.customer_id,
        },
        false,
      );

      if (openTableRes.status !== StatusCode.OK) {
        dispatch(dequeue());
        uiActions.showError(t("Error opening table"));
        return;
      }

      const firstCart = openTableRes?.data?.carts[0];
      const lineItemVariants: Array<{ variant_id: number; quantity: number }> = [];
      const seenVariantIds: Array<number> = [];
      (segment?.blocks ?? []).forEach(block => {
        const variantId = block?.product_variant_id;
        if (variantId == null || seenVariantIds.indexOf(variantId) !== -1) {
          return;
        }
        seenVariantIds.push(variantId);
        lineItemVariants.push({ variant_id: variantId, quantity: 1 });
      });
      if (firstCart && lineItemVariants.length > 0) {
        const lineItemRes = await PostLineItemToCart({ cart_id: firstCart?.id, variants: lineItemVariants }, false);
        if (lineItemRes?.status !== StatusCode.OK) {
          dispatch(dequeue());
          uiActions.showError(t("Error adding line item to cart"));
          return;
        }
      }

      const bookingRes = await PutReservationBooking(
        {
          id: segment?.booking_id,
          segment_ids: durations[bookingState?.durationKey],
          date: formatDate(date),
          quantity: bookingState?.guestsAmount,
          check_in_status: "checked",
          table_id: openTableState?.tableId,
          reservation_participants: booking?.participants,
        },
        false,
      );

      if (bookingRes?.status !== StatusCode.OK) {
        dispatch(dequeue());
        dispatch(showError(t("Error checking in customer")));
        return;
      }
      dispatch(dequeue());

      setOpenedTableId(tableId);
      setEditModalPage(Page.EDIT_BOOKING);
      void onSave();
    } else {
      uiActions.showError(t("Select a register to open a table"));
    }
  }

  function onBackButton() {
    ReactDOM.unstable_batchedUpdates(() => {
      setEditModalPage(Page.EDIT_BOOKING);
      setOpenTableState({ tableId: null, seatsAmount: 1 });
    });
  }

  async function navigateToTable() {
    if (bookingState?.tableId) {
      void closeBookingModal();
      await dispatch(loadActiveTableNew(bookingState?.tableId, true));
      history.push("/admin/table-service");
    }
  }

  async function reduceGuestFeesInCart() {
    const cart = orderCartRef.current;
    if (!cart?.line_items?.length) {
      return;
    }
    const feeVariantIds = new Set(displayFeeGroups.map(fee => fee.variantId).filter(Boolean));
    let latestCart = cart;
    for (const item of cart.line_items) {
      if (!feeVariantIds.has(item.variant_id) || item.parent_id) {
        continue;
      }
      if ((item.quantity || 1) <= 1) {
        const deleteRes = await DeleteLineItemToCart({ id: item.id }, false);
        if (deleteRes.status === StatusCode.OK) {
          latestCart = deleteRes.data as ICart;
        }
      } else {
        const updateRes = await PutLineItemToCart({ id: item.id, quantity: item.quantity - 1 }, false);
        if (updateRes.status === StatusCode.OK) {
          latestCart = updateRes.data;
        }
      }
    }
    rememberOrderCart(latestCart);
  }

  async function confirmRemoveGuest() {
    if (removeGuestIndex == null || removeGuestIndex === 0) {
      return;
    }
    const removed = bookingState.bookingParticipants[removeGuestIndex];
    const previousParticipants = bookingState.bookingParticipants;
    const previousAmount = bookingState.guestsAmount;
    const bookingParticipants = previousParticipants.filter((_, index) => index !== removeGuestIndex);
    const guestsAmount = clampReservationGuestCount(bookingParticipants.length, maxBookingSize);
    setRemoveGuestIndex(null);
    setBookingState(prevState => ({ ...prevState, bookingParticipants, guestsAmount }));
    await reduceGuestFeesInCart();
    const saved = await updateBooking(true, {
      bookingParticipants,
      guestsAmount,
      successMessage: t("{{name}} was removed from this booking.", {
        name: removed?.full_name || t("Guest"),
      }),
    });
    if (!saved) {
      setBookingState(prevState => ({
        ...prevState,
        bookingParticipants: previousParticipants,
        guestsAmount: previousAmount,
      }));
    }
  }

  function assignAnotherGuest() {
    if (removeGuestIndex == null) {
      return;
    }
    setChangeGuestCustomer(null);
    setChangeGuestResults([]);
    setChangeGuestIndex(removeGuestIndex);
    setRemoveGuestIndex(null);
  }

  async function confirmChangeGuest() {
    if (changeGuestIndex == null || !changeGuestCustomer) {
      return;
    }
    const current = bookingState.bookingParticipants[changeGuestIndex];
    const bookingParticipants = bookingState.bookingParticipants.map((participant, index) =>
      index === changeGuestIndex
        ? {
            ...changeGuestCustomer,
            participant_id: undefined,
            guest: index === 0 ? participant.guest : 1,
            check_in_status: participant.check_in_status || "unchecked",
          }
        : participant,
    );
    const previousName = current?.full_name;
    const nextName = changeGuestCustomer.full_name;
    setChangeGuestIndex(null);
    setChangeGuestCustomer(null);
    setBookingState(prevState => ({ ...prevState, bookingParticipants }));
    await updateBooking(true, {
      bookingParticipants,
      successMessage: t("{{previous}} replaced with {{next}} successfully", {
        previous: previousName,
        next: nextName,
      }),
    });
  }

  async function searchChangeGuest(query: string) {
    if (!query) {
      setChangeGuestResults([]);
      return;
    }
    const results = await searchCustomers(query);
    setChangeGuestResults(results ?? []);
  }

  const handleCustomerSearch = (query: string, slotIndex: number) => {
    ReactDOM.unstable_batchedUpdates(() => {
      setBookingState(prevState => {
        const guestQueries = [...prevState.guestQueries];
        guestQueries[slotIndex] = query;
        return { ...prevState, guestQueries };
      });
      setNewCustomerState(prevState => ({ ...prevState, currentSearch: query }));
    });
  };

  function openNewCustomerSheet(slotIndex?: number) {
    newCustomerSlotRef.current = slotIndex ?? null;
    setNewCustomerState(prevState => ({
      ...prevState,
      newCustomerSheetActive: true,
    }));
  }

  function openEditForm() {
    setEditStartTime(segment?.booking?.start_time || segment?.start_time || "");
    setEditDurations(null);
    setEditFormSnapshot({
      guestsAmount: bookingState.guestsAmount,
      durationKey: bookingState.durationKey,
      bookingParticipants: [...bookingState.bookingParticipants],
    });
    setEditFormOpen(true);
  }

  function changeAddGuestCount(count: number) {
    const maxAdd = Math.max(1, maxBookingSize - bookingState.guestsAmount);
    const next = Math.min(Math.max(1, count), maxAdd);
    setAddGuestCount(next);
    setPendingGuests(prevState => {
      const nextGuests = prevState.slice(0, next);
      while (nextGuests.length < next) {
        nextGuests.push(null);
      }
      return nextGuests;
    });
  }

  function openAddGuests() {
    if (bookingState.guestsAmount >= maxBookingSize) {
      return;
    }
    setAddGuestCount(1);
    setPendingGuests([null]);
    setAddGuestOpen(true);
  }

  function selectPendingGuest(offset: number, customer: ICustomer | null) {
    setPendingGuests(prevState => {
      const nextGuests = [...prevState];
      nextGuests[offset] = customer;
      return nextGuests;
    });
  }

  function cancelAddGuests() {
    setAddGuestOpen(false);
    setAddGuestCount(1);
    setPendingGuests([]);
    newCustomerSlotRef.current = null;
  }

  async function confirmAddGuests() {
    const holder = bookingState.bookingParticipants[0];
    const addedGuests = Array.from({ length: addGuestCount }, (_, offset) => {
      const selectedGuest = pendingGuests[offset];
      const customer = selectedGuest ?? holder;
      if (!customer) {
        return null;
      }
      return {
        ...customer,
        participant_id: undefined,
        guest: selectedGuest ? 1 : customer.guest,
        check_in_status: "unchecked",
      };
    }).filter((guest): guest is NonNullable<typeof guest> => Boolean(guest));
    const bookingParticipants = [...bookingState.bookingParticipants, ...addedGuests];
    const guestsAmount = bookingState.guestsAmount + addGuestCount;
    setAddGuestOpen(false);
    setAddGuestCount(1);
    setPendingGuests([]);
    setBookingState(prevState => ({
      ...prevState,
      guestsAmount,
      bookingParticipants,
      ...createEmptyGuestSearchState<ICustomer>(maxBookingSize),
    }));
    window.requestAnimationFrame(() => {
      const guestList = guestsListRef.current;
      if (guestList) {
        guestList.scrollTop = guestList.scrollHeight;
      }
    });
    await updateBooking(true, {
      bookingParticipants,
      guestsAmount,
      successMessage: t(addGuestCount === 1 ? "Guest added" : "Guests added"),
    });
  }

  function cancelEditForm() {
    if (editFormSnapshot) {
      setBookingState(prevState => ({
        ...prevState,
        guestsAmount: editFormSnapshot.guestsAmount,
        durationKey: editFormSnapshot.durationKey,
        bookingParticipants: editFormSnapshot.bookingParticipants,
      }));
    }
    setEditFormOpen(false);
    setEditFormSnapshot(null);
  }

  function deriveBookingCheckInStatus(participants: IBookingState["bookingParticipants"]) {
    const statuses = participants.map(participant => participant?.check_in_status || "unchecked");
    if (statuses.length > 0 && statuses.every(status => status === "checked")) {
      return "checked";
    }
    if (statuses.length > 0 && statuses.every(status => status === "no_show" || status === "no_show_charge")) {
      return "no_show";
    }
    return "unchecked";
  }

  async function applyParticipantCheckIn(
    bookingParticipants: IBookingState["bookingParticipants"],
    options?: { useGlobalLoader?: boolean },
  ) {
    const checkInStatus = deriveBookingCheckInStatus(bookingParticipants);
    setBookingState(prevState => ({ ...prevState, bookingParticipants, checkInStatus }));
    await updateBooking(true, { bookingParticipants, checkInStatus, useGlobalLoader: options?.useGlobalLoader });
  }

  async function setParticipantCheckInStatus(index: number, status: string, showButtonSpinner = false) {
    if (checkingInIndex != null) {
      return;
    }
    if (showButtonSpinner) {
      setCheckingInIndex(index);
    }
    try {
      await applyParticipantCheckIn(
        bookingState.bookingParticipants.map((participant, participantIndex) =>
          participantIndex === index ? { ...participant, check_in_status: status } : participant,
        ),
        { useGlobalLoader: !showButtonSpinner },
      );
    } finally {
      if (showButtonSpinner) {
        setCheckingInIndex(null);
      }
    }
  }

  function checkInAllParticipants() {
    const bookingParticipants = bookingState.bookingParticipants.map(participant => ({
      ...participant,
      check_in_status: "checked",
    }));
    const checkInStatus = deriveBookingCheckInStatus(bookingParticipants);
    setBookingState(prevState => ({ ...prevState, bookingParticipants, checkInStatus }));
    void updateBooking(true, {
      bookingParticipants,
      checkInStatus,
      successMessage: t("All guests are successfully checked in"),
    });
  }

  function renderCustomerOptions(slotIndex: number) {
    return (
      <>
        <div className="ui-select-dropdown-list-item" onClick={() => openNewCustomerSheet(slotIndex)}>
          <p>{t("New Customer")}</p>
        </div>
        {(bookingState.guestSearchResults[slotIndex] ?? []).map((customer: ICustomer, customerIndex: number) => {
          return (
            <Option key={customerIndex} value={customer?.id} extraValues={customer}>
              <div className="flex justify-between">
                <div>
                  <div className="font-semibold text-lg">{customer?.full_name}</div>
                  <div className="text-sm text-gray-500">{customer?.customer_type}</div>
                  <div className="text-sm text-gray-500">{customer?.email}</div>
                </div>
                <div className="font-medium text-base text-gray-500 self-end">{customer.member_code}</div>
              </div>
            </Option>
          );
        })}
      </>
    );
  }

  if (!open) {
    return null;
  }

  const startMoment = moment(segment?.booking?.start_time || segment?.start_time, "HH:mm:ss");
  const durationHours = parseFloat(bookingState.durationKey);
  const endMoment = startMoment.isValid() && durationHours ? startMoment.clone().add(durationHours, "hours") : null;
  const timeRangeLabel = startMoment.isValid()
    ? endMoment
      ? `${startMoment.format("h:mm")} - ${endMoment.format("h:mm A")}`
      : startMoment.format("h:mm A")
    : "";
  return (
    <>
      <div className="reservation-booking-details">
        <div className="reservation-booking-details__main">
          <div className="reservation-booking-details__header">
            <div className="reservation-booking-details__heading">
              <button type="button" className="reservation-booking-details__back" onClick={() => closeBookingModal()}>
                <FontAwesomeIcon icon={faArrowLeft} />
              </button>
              <div className="reservation-booking-details__title">
                <span>{timeRangeLabel}</span>
                <span className="reservation-booking-details__divider" />
                {location?.title ? <Badge size="small">{location.title}</Badge> : null}
                {location?.title ? <span className="reservation-booking-details__divider" /> : null}
                <span className="reservation-booking-details__date">{moment(date).format("MMMM Do, YYYY")}</span>
              </div>
            </div>
            <div className="reservation-booking-details__header-actions">
              <Button
                className="reservation-booking-details__check-in"
                type="secondary"
                size="medium"
                onClick={() =>
                  bookingState?.tableId ? void navigateToTable() : setEditModalPage(Page.OPEN_TABLE)
                }
              >
                <FontAwesomeIcon icon={faPlateUtensils} />
                {bookingState?.tableId ? t("View Table") : t("Check In & Open Table")}
              </Button>
              <NavigationDropdownNew
                type="secondary"
                label={t("Options")}
                rightAlign
                menuFitContent
                sections={[
                  [
                    {
                      label: t("Edit booking"),
                      icon: "pen",
                      onClick: openEditForm,
                    },
                  ],
                  [
                    {
                      label: t("Resend email confirmation"),
                      icon: "envelope",
                      onClick: () => void resendReservationConfirmation(),
                    },
                    {
                      label: t("Move booking"),
                      icon: "arrow-right-arrow-left",
                      onClick: openMoveBooking,
                    },
                  ],
                  [
                    {
                      label: isPending ? t("Decline Request") : t("Delete booking"),
                      icon: "trash-can",
                      color: "red",
                      onClick: handleCancelBooking,
                    },
                  ],
                ]}
              />
            </div>
          </div>

          <div className="reservation-delete-booking">
            <Sheet
              size="modal"
              open={bookingState.cancelPopoverVisible}
              closable
              stacked
              title={
                <div className="reservation-confirm-modal__heading">
                  <span className="reservation-confirm-modal__icon reservation-confirm-modal__icon--danger">
                    <FontAwesomeIcon icon={faTrashCan} />
                  </span>
                </div>
              }
              cancelText={t("Cancel")}
              okText={isPending ? t("Decline Request") : t("Delete")}
              onCancel={() => setBookingState(prevState => ({ ...prevState, cancelPopoverVisible: false }))}
              onOk={() => void confirmCancelBooking()}
            >
              <div className="reservation-confirm-modal__copy">
                <h2>{isPending ? t("Decline Request") : t("Delete booking")}</h2>
                <p>
                  {isPending
                    ? t("This will decline the customer's reservation request and release the time.")
                    : t("Are you sure you want to delete this booking? This action cannot be undone.")}
                </p>
                <Toggle
                  size="small"
                  checked={bookingState.sendCancellationEmail}
                  onChange={e => setBookingState(prev => ({ ...prev, sendCancellationEmail: e.target.checked }))}
                  label={isPending ? t("Send decline email") : t("Send cancellation email")}
                />
              </div>
            </Sheet>
          </div>

          <div className="reservation-booking-details__sections">
            {isPending && (
              <Callout
                type="warning"
                title={t("This reservation is waiting for approval")}
                content={t("Approve the request to confirm the booking, or decline it to release the time.")}
                actions={{
                  primary: { label: t("Approve Booking"), onClick: () => void acceptBooking() },
                }}
              />
            )}

            <section className="reservation-booking-details__card reservation-booking-details__card--guests">
              <div className="reservation-booking-details__card-header">
                <h2>{t("Guests ({{count}})", { count: bookingState.guestsAmount })}</h2>
                <div className="reservation-booking-details__card-actions">
                  <button
                    type="button"
                    className="reservation-booking-details__text-button reservation-booking-details__text-button--small"
                    disabled={bookingState.bookingParticipants.length === 0}
                    onClick={checkInAllParticipants}
                  >
                    {t("Check in all")}
                  </button>
                  <span className="reservation-booking-details__divider" />
                  <button
                    type="button"
                    className="reservation-booking-details__text-button reservation-booking-details__text-button--small"
                    disabled={bookingState.guestsAmount >= maxBookingSize}
                    onClick={openAddGuests}
                  >
                    {t("Add guest")}
                  </button>
                </div>
              </div>
              <div className="reservation-booking-details__guests" ref={guestsListRef}>
                {bookingState.bookingParticipants.map((participant, index) => {
                  const name = participant?.full_name ?? "";
                  const initials = name
                    .split(" ")
                    .filter(Boolean)
                    .slice(0, 2)
                    .map(part => part[0])
                    .join("")
                    .toUpperCase();

                  return (
                    <div className="reservation-booking-guest" key={participant?.participant_id ?? index}>
                      <div className="reservation-booking-guest__identity">
                        <div className="reservation-booking-guest__avatar">{initials}</div>
                        <div>
                          <div className="reservation-booking-guest__name">
                            {name}
                            {participant?.check_in_status === "no_show" || participant?.check_in_status === "no_show_charge" ? (
                              <FontAwesomeIcon icon={faBan} className="reservation-booking-guest__no-show" />
                            ) : null}
                            {index === 0 ? <Badge size="small" type="gray">{t("Booking owner")}</Badge> : null}
                          </div>
                          {[participant?.customer_type, participant?.member_code].filter(Boolean).length ? (
                            <div className="reservation-booking-guest__meta">
                              {[participant?.customer_type, participant?.member_code].filter(Boolean).join(" • ")}
                            </div>
                          ) : null}
                          {participant?.email ? (
                            <div className="reservation-booking-guest__meta">{participant.email}</div>
                          ) : null}
                        </div>
                      </div>
                      <div className="reservation-booking-guest__actions">
                        <Button
                          className="reservation-booking-guest__check-in"
                          type={participant?.check_in_status === "checked" ? "primary" : "secondary"}
                          size="small"
                          loading={checkingInIndex === index}
                          onClick={() =>
                            void setParticipantCheckInStatus(
                              index,
                              participant?.check_in_status === "checked" ? "unchecked" : "checked",
                              true,
                            )
                          }
                        >
                          {participant?.check_in_status === "checked" ? t("Checked in") : t("Check in")}
                          {participant?.check_in_status === "checked" && checkingInIndex !== index ? (
                            <FontAwesomeIcon icon={faCheck} />
                          ) : null}
                        </Button>
                        <NavigationDropdownNew
                          showPlainTextLabel
                          hideChevron
                          rightAlign
                          type="secondary"
                          label={<FontAwesomeIcon icon={faEllipsisVertical} />}
                          sections={[
                            [
                              {
                                label: participant?.check_in_status === "checked" ? t("Not checked in") : t("Check In"),
                                icon: "circle-check",
                                onClick: () =>
                                  setParticipantCheckInStatus(
                                    index,
                                    participant?.check_in_status === "checked" ? "unchecked" : "checked",
                                  ),
                              },
                              {
                                label:
                                  participant?.check_in_status === "no_show" || participant?.check_in_status === "no_show_charge"
                                    ? t("Remove no show")
                                    : t("No show"),
                                icon: "ban",
                                onClick: () =>
                                  setParticipantCheckInStatus(
                                    index,
                                    participant?.check_in_status === "no_show" || participant?.check_in_status === "no_show_charge"
                                      ? "unchecked"
                                      : "no_show",
                                  ),
                              },
                              {
                                label: t("Charge no show"),
                                icon: "dollar-sign",
                                hidden: participant?.check_in_status !== "no_show" && participant?.check_in_status !== "no_show_charge",
                                onClick: () => setParticipantCheckInStatus(index, "no_show_charge"),
                              },
                            ],
                            [
                              {
                                label: t("Change guest"),
                                icon: "arrow-right-arrow-left",
                                onClick: () => {
                                  setChangeGuestCustomer(null);
                                  setChangeGuestResults([]);
                                  setChangeGuestIndex(index);
                                },
                              },
                            ],
                            [
                              {
                                label: t("Remove guest"),
                                icon: "user-xmark",
                                hidden: index === 0,
                                onClick: () => setRemoveGuestIndex(index),
                              },
                            ],
                          ]}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              {addGuestOpen ? (
                <div className="reservation-add-guests">
                  <div className="reservation-add-guests__header">
                    <h3>{t("Add guest(s)")}</h3>
                    <div className="reservation-add-guests__stepper">
                      <button
                        type="button"
                        aria-label={t("Remove guest")}
                        disabled={addGuestCount <= 1}
                        onClick={() => changeAddGuestCount(addGuestCount - 1)}
                      >
                        <FontAwesomeIcon icon={faMinus} />
                      </button>
                      <span>{addGuestCount}</span>
                      <button
                        type="button"
                        aria-label={t("Add guest")}
                        disabled={bookingState.guestsAmount + addGuestCount >= maxBookingSize}
                        onClick={() => changeAddGuestCount(addGuestCount + 1)}
                      >
                        <FontAwesomeIcon icon={faPlus} />
                      </button>
                    </div>
                  </div>
                  {Array.from({ length: addGuestCount }, (_, offset) => {
                    const slotIndex = bookingState.guestsAmount + offset;
                    const pendingGuest = pendingGuests[offset];
                    return (
                      <div className="reservation-add-guests__field" key={slotIndex}>
                        <span>{t("Guest {{number}}", { number: slotIndex + 1 })}</span>
                        {pendingGuest ? (
                          <div className="reservation-add-guests__selected">
                            <span>{pendingGuest.full_name}</span>
                            <button
                              type="button"
                              aria-label={t("Remove guest")}
                              onClick={() => selectPendingGuest(offset, null)}
                            >
                              <FontAwesomeIcon icon={faXmark} />
                            </button>
                          </div>
                        ) : (
                          <Select
                            showSearch
                            onSearch={(query: string) => handleCustomerSearch(query, slotIndex)}
                            onChange={(_id: number, customer: ICustomer) => selectPendingGuest(offset, customer)}
                            searchValue={bookingState.guestQueries[slotIndex] ?? ""}
                            showDropDownOnFocus
                            searching={bookingState.searching}
                            placeholder={t("Search")}
                            usePortal
                            leftIcon={<FontAwesomeIcon className="reservation-add-guests__search-icon" icon={faMagnifyingGlass} />}
                          >
                            {renderCustomerOptions(slotIndex)}
                          </Select>
                        )}
                      </div>
                    );
                  })}
                  <div className="reservation-add-guests__footer">
                    <Button type="secondary" size="medium" onClick={cancelAddGuests}>
                      {t("Cancel")}
                    </Button>
                    <Button type="primary" size="medium" onClick={() => void confirmAddGuests()}>
                      {t("Update changes")}
                    </Button>
                  </div>
                </div>
              ) : null}
            </section>

            <section className="reservation-booking-details__card">
              <div className="reservation-booking-details__card-header">
                <h2>{t("Bay fee(s)")}</h2>
              </div>
              {reservationOrderId ? (
                <button
                  type="button"
                  className="reservation-booking-details__fee-order"
                  onClick={() => history.push(`/admin/order/${reservationOrderId}`)}
                >
                  {reservationOrderStatus === "paid" ? (
                    <span className="reservation-booking-details__fee-paid">
                      <FontAwesomeIcon icon={faUsdCircle} />
                    </span>
                  ) : null}
                  <span>
                    {t("Order")} {reservationOrderName} {reservationOrderStatus}
                  </span>
                </button>
              ) : reservationOrderStatus === "paid" ? (
                <div className="reservation-booking-details__fee-order">
                  <span className="reservation-booking-details__fee-paid">
                    <FontAwesomeIcon icon={faUsdCircle} />
                  </span>
                  <span>{t("Paid")}</span>
                </div>
              ) : null}
              <p className="reservation-booking-details__support">
                {t("Fees are added based on the segment and booking duration. Change the fee type or variant if needed.")}
              </p>
              <div className="reservation-booking-details__fee-list">
                {displayFeeGroups.map((fee, feeIndex) => {
                  const groupKey = `group:${feeIndex}`;
                  const isAddingGroup = addingFeeTarget === groupKey;
                  const segmentLabel = t(fee.quantity === 1 ? "{{count}} segment" : "{{count}} segments", {
                    count: fee.quantity,
                  });
                  const options = groupFeeOptions(fee);
                  const groupHasRemainingFees = options.some(
                    option =>
                      option.variantId &&
                      !paidVariantIds.has(option.variantId) &&
                      !orderCart?.line_items?.some(item => item.variant_id === option.variantId),
                  );

                  return (
                    <div className="reservation-booking-details__fee-group" key={`${fee.variantId ?? "none"}-${fee.timeLabel}`}>
                      {displayFeeGroups.length > 1 && fee.timeLabel ? (
                        <div className="reservation-booking-details__fee-range">
                          <FontAwesomeIcon icon={faClock} />
                          <span>{t("Fees for {{time}}", { time: fee.timeLabel })}</span>
                        </div>
                      ) : null}
                      <div className="reservation-booking-details__fee-row">
                      <div className="reservation-booking-details__fee-options">
                        {options.map(option => {
                          const feePaid = Boolean(option.variantId && paidVariantIds.has(option.variantId));
                          const addedToCart = Boolean(
                            option.variantId && orderCart?.line_items?.some(item => item.variant_id === option.variantId),
                          );
                          const isAddingOption = addingFeeTarget === `variant:${option.variantId}`;
                          const variantLabel =
                            option.variantTitle && option.variantTitle !== option.productTitle ? option.variantTitle : null;
                          const feeMeta = [variantLabel, segmentLabel].filter(Boolean).join(" • ");

                          return (
                            <div
                              className={classNames("reservation-booking-segment-fee", { "is-paid": feePaid })}
                              key={`${option.variantId ?? "none"}-${fee.timeLabel}`}
                            >
                              <div className="reservation-booking-segment-fee__copy">
                                {option.variantId ? (
                                  <>
                                    <div className="reservation-booking-segment-fee__title">
                                      <span>{option.productTitle}</span>
                                      {feePaid ? (
                                        <Badge size="small" type="success">
                                          {t("Paid")}
                                        </Badge>
                                      ) : null}
                                    </div>
                                    {feeMeta ? <div className="reservation-booking-segment-fee__meta">{feeMeta}</div> : null}
                                  </>
                                ) : (
                                  <div className="reservation-booking-segment-fee__empty">{t("No fee")}</div>
                                )}
                              </div>
                              <button
                                type="button"
                                className={classNames("reservation-booking-segment-fee__action", {
                                  "is-selected": addedToCart,
                                  "is-paid": feePaid,
                                  "is-loading": isAddingOption,
                                })}
                                disabled={feePaid || !option.variantId || (addingFee && !isAddingOption)}
                                aria-label={feePaid ? t("Paid") : addedToCart ? t("Remove fee") : t("Add fee")}
                                onClick={() => {
                                  if (feePaid) {
                                    return;
                                  }
                                  if (addedToCart) {
                                    void removeSegmentFeeFromCart(option.variantId);
                                    return;
                                  }
                                  void addSingleFee({
                                    ...fee,
                                    variantId: option.variantId,
                                    productTitle: option.productTitle,
                                    variantTitle: option.variantTitle,
                                  });
                                }}
                              >
                                {isAddingOption ? <Spin /> : <FontAwesomeIcon icon={feePaid || addedToCart ? faCheck : faPlus} />}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                      <button
                        type="button"
                        className={classNames("reservation-booking-details__fee-cart", { "is-loading": isAddingGroup })}
                        disabled={(addingFee && !isAddingGroup) || (!isAddingGroup && !groupHasRemainingFees)}
                        aria-label={t("Add all fees")}
                        onClick={() => void addFeesForGroup(fee, groupKey)}
                      >
                        {isAddingGroup ? <Spin /> : <FontAwesomeIcon icon={faCartShopping} />}
                      </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="reservation-booking-details__card">
              <div className="reservation-booking-details__card-header">
                <h2>{t("secure.facility.reservation.reservation_booking_modal_edit.025")}</h2>
              </div>
              <p className="reservation-booking-details__support">
                {t("Select a saved card on file or add one manually.")}
              </p>
              {bookingState.customerCreditCards?.length >= 1 ? (
                <div className="ui-checkbox-group">
                  {bookingState.customerCreditCards?.map((paymentMethod: Record<string, any>, index: number) => {
                    return (
                      <div
                        key={index}
                        onClick={() => void onSelectCreditCard(Number(paymentMethod.id))}
                        className={classNames("ui-checkbox-group-item", {
                          "ui-checkbox-group-item_selected": Number(paymentMethod.id) === Number(bookingState?.selectedCardId),
                        })}
                      >
                        <div className="ui-checkbox-group-item-content">
                          <div className="ui-checkbox-group-item-text">
                            <p className="text-sm text-medium text-gray-700">
                              {valueToString(String(paymentMethod.brand), "capitalize")}{" "}
                              {t("secure.facility.reservation.reservation_booking_modal_edit.043")} {paymentMethod.last4}
                            </p>
                            <p className="text-sm text-regular text-gray-600">
                              {t("secure.facility.reservation.reservation_booking_modal_edit.044")} {paymentMethod.expiry}
                            </p>
                          </div>
                        </div>
                        <div style={{ pointerEvents: "none" }}>
                          <Checkbox
                            size="medium"
                            value={paymentMethod.id}
                            checked={Number(paymentMethod.id) === Number(bookingState?.selectedCardId)}
                            onChange={() => undefined}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="reservation-booking-details__empty">
                  <FontAwesomeIcon icon={faCircleInfo} />
                  <div>
                    <div className="reservation-booking-details__empty-title">{t("Add a credit card")}</div>
                    <div className="reservation-booking-details__empty-support">
                      {t("Customer does not have any saved credit card on their account.")}
                    </div>
                  </div>
                </div>
              )}
              {bookingState.addCard && open ? (
                <div className="reservation-booking-details__add-card">
                  <CardSection onChange={handleCardSectionChange} type="one-row" />
                  <Checkbox
                    label={t("secure.facility.reservation.reservation_booking_modal_edit.029")}
                    size="small"
                    value={bookingState.saveCard}
                    checked={bookingState.saveCard}
                    onChange={() => setBookingState(prevState => ({ ...prevState, saveCard: !prevState.saveCard }))}
                  />
                  <Button type="primary" size="medium" disabled={!elementsComplete} onClick={() => void saveNewCard()}>
                    {t("Save card")}
                  </Button>
                </div>
              ) : null}
              <button
                type="button"
                className="reservation-booking-details__text-button"
                onClick={() =>
                  setBookingState(prevState => ({
                    ...prevState,
                    addCard: !prevState.addCard,
                    elementComplete: prevState.addCard
                      ? { cardNumber: false, cardExpiry: false, cardCvc: false }
                      : prevState.elementComplete,
                  }))
                }
              >
                <FontAwesomeIcon icon={faPlus} />
                {bookingState.addCard
                  ? t("secure.facility.reservation.reservation_booking_modal_edit.031")
                  : t("secure.facility.reservation.reservation_booking_modal_edit.030")}
              </button>
            </section>

            <section className="reservation-booking-details__card">
              <div className="reservation-booking-details__card-header">
                <h2>
                  {t("Notes ({{count}})", {
                    count: (bookingState.bookingNotes?.length ?? 0) + (highPriorityNotes?.length ?? 0),
                  })}
                </h2>
              </div>
              <div className="reservation-booking-details__notes">
                {!bookingState?.bookingNotes?.length && !highPriorityNotes?.length ? (
                  <div className="reservation-booking-details__empty">
                    <FontAwesomeIcon icon={faCircleInfo} />
                    <div>
                      <div className="reservation-booking-details__empty-title">{t("Add a booking note")}</div>
                      <div className="reservation-booking-details__empty-support">
                        {t("There are no saved notes for this booking.")}
                      </div>
                    </div>
                  </div>
                ) : null}
                {bookingState?.bookingNotes?.map((note, index) => {
                  return (
                    <Note
                      key={index}
                      authorName={note?.author?.full_name}
                      dateString={moment(note?.created_at).format("LLL")}
                      content={note?.content}
                    />
                  );
                })}
                {highPriorityNotes?.map((highPriorityNote, noteIndex) => {
                  return (
                    <Note
                      key={`priority-${noteIndex}`}
                      authorName={highPriorityNote.author?.full_name}
                      content={highPriorityNote.content}
                      dateString={moment(highPriorityNote.created_at).format("LLL")}
                      highPriority={highPriorityNote.high_priority}
                      quickNote
                    />
                  );
                })}
              </div>
              <button
                type="button"
                className="reservation-booking-details__text-button"
                onClick={() => setBookingState(prevState => ({ ...prevState, addNewNoteVisible: true }))}
              >
                <FontAwesomeIcon icon={faPlus} />
                {t("Add note")}
              </button>
            </section>
          </div>
        </div>

        <div
          className={classNames("reservation-booking-details__order", {
            "reservation-booking-details__order--cart": Boolean(orderCart),
          })}
        >
          {orderCart ? (
            <RegisterContext>
              <div className="booking-details-cart-container">
                <CartMenuNew
                  dataOrigin={{
                    stateCart: orderCart,
                    clearStateCart: () => rememberOrderCart(null),
                    updateState: updateCartOriginState,
                  }}
                />
              </div>
            </RegisterContext>
          ) : (
            <>
              <div className="reservation-booking-details__order-icon">
                <FontAwesomeIcon icon={faFileCirclePlus} />
              </div>
              <div className="reservation-booking-details__order-title">{t("Create new order")}</div>
              <div className="reservation-booking-details__order-description">
                {t("Click the plus on a segment fee to start a new order.")}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="reservation-move-booking">
        <Sheet
          size="modal"
          open={bookingState.copyPopoverVisible}
          closable
          stacked
          title={t("Move booking")}
          supportText={t("Move this booking to another date, time and location with the same duration.")}
          cancelText={t("Cancel")}
          okText={t("Move booking")}
          onCancel={closeMoveBooking}
          onOk={() => void moveBooking()}
          okDisabled={!bookingState.copy_date || !bookingState.copy_location_id || !bookingState.copy_time}
        >
          <div className="reservation-move-booking__fields" key={moveBookingKey}>
            <DatePickerInput
              label={t("Date")}
              months={1}
              position="left"
              startingDate={bookingState.copy_date || undefined}
              allowEmptySingleDate
              disablePastDays
              usePortal
              setStartingDate={value => {
                const nextDate = typeof value === "function" ? value(bookingState.copy_date ?? new Date()) : value;
                if (nextDate instanceof Date) {
                  syncDateWithState(nextDate);
                }
              }}
            />
            <Select
              label={t("Location")}
              placeholder={t("Select a bay location")}
              showPlaceholder
              block
              usePortal
              leftIcon={<FontAwesomeIcon className="reservation-move-booking__field-icon" icon={faLocationDot} />}
              onChange={(incomingValue: number) =>
                setBookingState(prevState => ({ ...prevState, copy_location_id: incomingValue, copy_time: "" }))
              }
            >
              {locations.map((moduleLocation: ILocation, index: number) => {
                return (
                  <Option key={index} value={moduleLocation.id}>
                    {moduleLocation.title}
                  </Option>
                );
              })}
            </Select>
            <Select
              key={`${moveBookingKey}-${bookingState.copy_location_id}-${bookingState.copy_date ? bookingState.copy_date.getTime() : "none"}`}
              label={t("Start time")}
              placeholder={t("Select time")}
              showPlaceholder
              block
              usePortal
              leftIcon={<FontAwesomeIcon className="reservation-move-booking__field-icon" icon={faClock} />}
              onChange={(incomingValue: string) => handleTimeChange(incomingValue)}
            >
              {durations &&
                availableSegments
                  ?.filter(
                    availableSegment =>
                      (availableSegment.availableHours ?? 0) >= durations[bookingState.durationKey]?.length,
                  )
                  ?.map(availableSegment => {
                    return (
                      <Option value={availableSegment.start_time ?? ""} key={availableSegment.id}>
                        {convertTime(availableSegment.start_time)}
                      </Option>
                    );
                  })}
            </Select>
          </div>
        </Sheet>
      </div>

      <div className="reservation-edit-booking">
      <Sheet
        size="modal"
        open={editFormOpen}
        closable
        stacked
        title={
          <div>
            <div>{t("Edit booking time")}</div>
            <div className="reservation-booking-edit-subtitle">
              {t("Update the date, start time, and duration to fit your schedule.")}
            </div>
          </div>
        }
        cancelText={t("Cancel")}
        okText={t("Update changes")}
        onCancel={cancelEditForm}
        onOk={() =>
          void updateBooking(true, {
            segmentIds: (editDurations ?? durations)?.[bookingState.durationKey],
            moveDate: formatDate(editDate),
          })
        }
        okDisabled={!(editDurations ?? durations)}
        backDropCancel={false}
      >
        <div className="reservation-edit-booking__fields">
          <DatePickerInput
            label={t("Date")}
            months={1}
            position="left"
            startingDate={editDate}
            setStartingDate={setEditDate}
            usePortal
          />
          <Select
            key={editStartTime || segment?.booking?.start_time || "start"}
            label={t("Start time")}
            defaultValue={editStartTime || segment?.booking?.start_time || segment?.start_time}
            usePortal
            leftIcon={<FontAwesomeIcon className="reservation-edit-booking__field-icon" icon={faClock} />}
            onChange={(value: string) => {
              const nextDurations = buildDurationsFromStart(editDaySegments, value, segment?.booking?.id);
              const nextKey = pickDurationKey(
                nextDurations,
                bookingState.durationKey,
                (editDurations ?? durations)?.[bookingState.durationKey]?.length,
              );
              setEditStartTime(value);
              setEditDurations(nextDurations);
              if (nextKey) {
                handleDropDownChange(nextKey, "durationKey");
              }
            }}
          >
            {editDaySegments
              .filter(daySegment => Object.keys(buildDurationsFromStart(editDaySegments, daySegment.start_time, segment?.booking?.id)).length > 0)
              .map(daySegment => (
                <Option key={daySegment.id} value={daySegment.start_time}>
                  {convertTime(daySegment.start_time)}
                </Option>
              ))}
          </Select>
          {editDurations ?? durations ? (
            <Select
              label={t("Duration")}
              defaultValue={bookingState?.durationKey}
              usePortal
              onChange={(value: string) => handleDropDownChange(value, "durationKey")}
            >
              {Object.keys(editDurations ?? durations).map((key, durationIndex) => {
                return (
                  <Option key={durationIndex} value={key}>
                    {formatDurationLabel(key)}
                  </Option>
                );
              })}
            </Select>
          ) : (
            <Spin />
          )}
        </div>
      </Sheet>
      </div>

      <div className="reservation-check-in-table">
      <Sheet
        size="modal"
        open={open && editModalPage === Page.OPEN_TABLE}
        closable
        stacked
        title={t("Check In & Open table")}
        cancelText={t("Cancel")}
        okText={t("Open table")}
        onOk={() => handleOpenTableCheckIn()}
        onCancel={() => onBackButton()}
        okDisabled={!openTableState.tableId || !openTableState.seatsAmount}
        backDropCancel={false}
      >
        <ReservationTableSelection
          guestsAmount={bookingState.guestsAmount}
          onTableSelect={(tableId, seatsAmount) => setOpenTableState({ tableId, seatsAmount })}
          selectedTableId={openTableState?.tableId}
        />
      </Sheet>
      </div>

      <div className="reservation-table-opened">
        <Sheet
          size="modal"
          open={openedTableId != null}
          closable
          stacked
          title={
            <span className="reservation-confirm-modal__icon reservation-confirm-modal__icon--success">
              <FontAwesomeIcon icon={faCircleCheck} />
            </span>
          }
          cancelText={t("Cancel")}
          okText={t("View table")}
          onCancel={() => setOpenedTableId(null)}
          onOk={() => {
            const tableId = openedTableId;
            setOpenedTableId(null);
            if (tableId) {
              void closeBookingModal();
              void onOpenTableSuccess(tableId);
            }
          }}
        >
          <div className="reservation-confirm-modal__copy">
            <h2>{t("Table opened successfully!")}</h2>
            <p>{t("Table opened and guests checked in successfully")}</p>
          </div>
        </Sheet>
      </div>

      <div className="reservation-remove-guest">
        <Sheet
          size="modal"
          open={removeGuestIndex != null}
          closable
          stacked
          title={
            <span className="reservation-confirm-modal__icon reservation-confirm-modal__icon--warning">
              <FontAwesomeIcon icon={faUserXmark} />
            </span>
          }
          cancelText={t("Assign another")}
          okText={t("Remove guest")}
          onClose={() => setRemoveGuestIndex(null)}
          onCancel={assignAnotherGuest}
          onOk={() => void confirmRemoveGuest()}
          backDropCancel={false}
        >
          <div className="reservation-confirm-modal__copy">
            <h2>{t("Remove guest?")}</h2>
            <p>
              {t("This will remove {{name}} from the booking and their green fees from the cart.", {
                name: bookingState.bookingParticipants[removeGuestIndex ?? 0]?.full_name,
              })}
            </p>
            <p>{t("The booking holder will take their place until another guest is assigned.")}</p>
          </div>
        </Sheet>
      </div>

      <div className="reservation-change-guest">
        <Sheet
          size="modal"
          open={changeGuestIndex != null}
          closable
          stacked
          overflow
          title={t("Change guest")}
          cancelText={t("Cancel")}
          okText={t("Change guest")}
          okDisabled={!changeGuestCustomer}
          onCancel={() => {
            setChangeGuestIndex(null);
            setChangeGuestCustomer(null);
          }}
          onOk={() => void confirmChangeGuest()}
        >
          <div className="reservation-change-guest__body">
            <div className="reservation-change-guest__label">{t("Current golfer:")}</div>
            {changeGuestIndex != null ? (
              <ChangeGuestCard
                name={bookingState.bookingParticipants[changeGuestIndex]?.full_name}
                email={bookingState.bookingParticipants[changeGuestIndex]?.email}
                phone={bookingState.bookingParticipants[changeGuestIndex]?.phone}
                memberCode={bookingState.bookingParticipants[changeGuestIndex]?.member_code}
                customerType={bookingState.bookingParticipants[changeGuestIndex]?.customer_type}
              />
            ) : null}
            <div className="reservation-change-guest__arrow">
              <FontAwesomeIcon icon={faArrowDown} />
            </div>
            <div className="reservation-change-guest__label">{t("New golfer:")}</div>
            {changeGuestCustomer ? (
              <ChangeGuestCard
                name={changeGuestCustomer.full_name}
                email={changeGuestCustomer.email}
                phone={changeGuestCustomer.phone}
                memberCode={changeGuestCustomer.member_code}
                customerType={changeGuestCustomer.customer_type}
                clearLabel={t("Search again")}
                onClear={() => setChangeGuestCustomer(null)}
              />
            ) : (
              <Select
                showSearch
                usePortal
                showDropDownOnFocus
                placeholder={t("Search by name, email, phone or member number...")}
                onSearch={(query: string) => void searchChangeGuest(query)}
                onChange={(_id: number, customer: ICustomer) => setChangeGuestCustomer(customer)}
              >
                {changeGuestResults.map(customer => (
                  <Option key={customer.id} value={customer.id} extraValues={customer}>
                    {customer.full_name}
                  </Option>
                ))}
              </Select>
            )}
          </div>
        </Sheet>
      </div>

      <OrderCompletePopup
        open={orderCompletePopup.isOpen}
        setOpen={resetCompletedOrder}
        order={completedOrder}
        returnable
        returnLabel={t("Return to Reservations")}
        onReturn={closeBookingModal}
        disableDuringLoad
        loading={orderCompletePopup.loading}
        transactionComplete={orderCompletePopup.transactionComplete}
      />

      <NewCustomer
        newCustomerSheetActive={newCustomerState.newCustomerSheetActive}
        onCancel={closeNewCustomer}
        onOk={info => void createNewCustomer(info)}
      />

      <Portal isMounted={bookingState.addNewNoteVisible}>
        <Sheet
          title={t("secure.facility.reservation.reservation_booking_modal_edit.065")}
          open={bookingState.addNewNoteVisible}
          size="small"
          onCancel={() => setBookingState(prevState => ({ ...prevState, addNewNoteVisible: false, note: "" }))}
          onOk={handleAddBookingNote}
          okDisabled={bookingState.note === ""}
          stacked
        >
          <div className="new-note_container">
            <TextField
              labelHidden
              autoFocus
              id="note"
              placeholder={t("secure.facility.reservation.reservation_booking_modal_edit.066")}
              rows={2}
              value={bookingState.note}
              onChange={handleInputChange}
            ></TextField>
          </div>
        </Sheet>
      </Portal>
    </>
  );
}
